import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Helmet } from "react-helmet-async";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { userSchema } from "../../utils/Schema";
import { userServices } from "../../services/AllServices";

function AddEditUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    if (/^\d/.test(pastedText)) {
      e.preventDefault();
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await userServices.getById({ id });
        setData(response?.data?.data?.result);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: userSchema,
    initialValues: {
      firstname: data?.firstname || "",
      lastname:  data?.firstname || "",
      username:  data?.username || "",
      email: data?.email || "",
      phoneNumber: data?.phoneNumber || "",
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          id: id || undefined,
          firstname: values.firstname.trim(),
          lastname: values.lastname.trim(),
          username: values.username.trim(),
          email: values.email,
          phoneNumber: values.phoneNumber,
        };

        const response = id
          ? await userServices.edit(payload)
          : await userServices.add(payload);
        toast.success(response?.data?.message, { toastId: "success" });
        
        // Navigate back to user management with preserved page and view mode
        const page = searchParams.get('page');
        const view = searchParams.get('view');
        
        if (page && view) {
          navigate(`/user-management?page=${page}&view=${view}`);
        } else if (page) {
          navigate(`/user-management?page=${page}`);
        } else if (view) {
          navigate(`/user-management?view=${view}`);
        } else {
          navigate(-1);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit } = formik;

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} User</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} User`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    required
                    name="firstname"
                    value={values.firstname}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    error={Boolean(touched.firstname && errors.firstname)}
                    helperText={touched.firstname && errors.firstname}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    required
                    name="lastname"
                    value={values.lastname}
                    onChange={handleChange}
                    error={Boolean(touched.lastname && errors.lastname)}
                    helperText={touched.lastname && errors.lastname}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="User Name"
                    required
                    name="username"
                    value={values.username}
                    onChange={handleChange}
                    error={Boolean(touched.username && errors.username)}
                    helperText={touched.username && errors.username}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    required
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    required
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    inputProps={{ maxLength: 15 }}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} User
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditUser;
